import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Tutorial } from "@/assets";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { PhaseFormFieldOption } from "../../phaseForm/types";

interface SelectFieldProps {
  value: string | string[];
  onChange: (value: string | string[]) => void;
  options: PhaseFormFieldOption[];
  hasErrors?: boolean;
  required?: boolean;
  label: string;
  description?: string;
  videoUrl?: string;
  multiline?: boolean;
  activeStep?: number;
  totalSteps?: number;
  className?: string;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  value,
  onChange,
  options,
  label,
  description,
  required = false,
  videoUrl,
  multiline = false,
  hasErrors,
  activeStep = 1,
  totalSteps = 25,
  className,
}) => {
  // Verifica se o campo é inválido baseado nas regras:
  // - Se não é multiline: required e sem valor
  // - Se é multiline: required e array vazio
  const isInvalid =
    required &&
    (multiline ? Array.isArray(value) && value.length === 0 : !value);

  const handleToggle = (optionValue: string) => {
    if (multiline) {
      // Se multiline, trata como array
      const currentValue = Array.isArray(value) ? value : [];
      const newValue = currentValue.includes(optionValue)
        ? currentValue.filter((v) => v !== optionValue)
        : [...currentValue, optionValue];
      onChange(newValue);
    } else {
      // Se não é multiline, apenas seleciona o valor
      onChange(optionValue);
    }
  };

  // Verifica se um item está selecionado
  const isSelected = (optionValue: string) => {
    if (multiline) {
      return Array.isArray(value) && value.includes(optionValue);
    }
    return value === optionValue;
  };

  return (
    <div className={className}>
      {label && (
        <h1 className="text-background font-bold text-sm sm:text-2xl mb-2.5 sm:mb-4">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </h1>
      )}
      {description && (
        <h2 className="text-grey-light text-sm sm:text-base mb-6 sm:mb-8">
          {description}
        </h2>
      )}

      <p className="text-grey-light text-xs sm:text-sm mb-4 italic">
        {multiline
          ? required
            ? "Selecione pelo menos uma opção"
            : "Selecione uma ou mais opções"
          : "Selecione uma opção"}
      </p>

      <div className="flex flex-col justify-center text-base sm:text-lg gap-1 w-full mb-4">
        {options.map((option) => (
          <div key={option.value} className="flex items-start gap-4 mb-1">
            <Checkbox
              className="w-5 h-5 border-2 data-[state=checked]:text-background mt-0.5"
              checked={isSelected(option.value)}
              onCheckedChange={(checked) => {
                if (checked) {
                  handleToggle(option.value);
                } else if (multiline || !required) {
                  // Permite desmarcar apenas se for multiline ou não obrigatório
                  handleToggle(option.value);
                }
              }}
            />
            <span className="text-background text-sm">
              {option.key || option.value}
            </span>
          </div>
        ))}
      </div>

      {isInvalid && (
        <div className="text-red-500 text-sm mt-2">
          {multiline
            ? "Selecione pelo menos uma opção"
            : "Este campo é obrigatório"}
        </div>
      )}

      {videoUrl && (
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              className="flex gap-2 items-center bg-background/10 rounded-md w-fit px-4 py-2 mt-4"
            >
              <Tutorial fill="var(--background)" />
              <span>Ver vídeo explicativo</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="aspect-video min-w-2/3 bg-black/90 rounded-2xl border border-gray-800 text-grey-light">
            <div
              style={{
                left: 0,
                width: "100%",
                height: 0,
                position: "relative",
                paddingBottom: "56.25%",
              }}
            >
              <iframe
                src={videoUrl}
                style={{
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  position: "absolute",
                  border: 0,
                }}
                allowFullScreen
                scrolling="no"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share;"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
